"use client";
import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useTheme } from '../utils/theming/useTheme.js';
import { Button } from '../Button/Button.web.js';
import { ListItems } from '../utils/components/ListItems/ListItems.web.js';
import { Box } from '../Box/Box.web.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { useOutsideClickClose } from '../utils/hooks/useOutsideClickClose.web.js';
import { IconChevronUp } from '../icons/ui_navigation/IconChevronUp.web.js';
import { IconChevronDown } from '../icons/ui_navigation/IconChevronDown.web.js';
import { useBoolean } from '../utils/hooks/useBoolean.js';
import { useComponentVariant } from '../utils/hooks/useComponentVariant.js';
import { px2rem } from '../utils/helpers/style.js';
import { spacing } from '../constants/spacing.js';
import { shadows } from '../constants/shadows.js';

var _excluded = ["icon", "color", "label", "dropdownDirection", "buttonProps", "iconPlacement", "menuWidth", "showLabel", "selected", "items", "hideExpandIcon", "onSelect", "variant", "aria-label", "id", "transparent", "menuHeader"];
function Menu(props) {
  var icon = props.icon,
    colorProp = props.color,
    labelProp = props.label,
    _props$dropdownDirect = props.dropdownDirection,
    dropdownDirection = _props$dropdownDirect === undefined ? 'down' : _props$dropdownDirect,
    buttonProps = props.buttonProps,
    _props$iconPlacement = props.iconPlacement,
    iconPlacement = _props$iconPlacement === undefined ? 'left' : _props$iconPlacement,
    _props$menuWidth = props.menuWidth,
    menuWidth = _props$menuWidth === undefined ? '12rem' : _props$menuWidth,
    _props$showLabel = props.showLabel,
    showLabel = _props$showLabel === undefined ? true : _props$showLabel,
    selected = props.selected,
    items = props.items,
    _props$hideExpandIcon = props.hideExpandIcon,
    hideExpandIcon = _props$hideExpandIcon === undefined ? false : _props$hideExpandIcon,
    onSelect = props.onSelect,
    variantProp = props.variant,
    ariaLabel = props['aria-label'],
    _props$id = props.id,
    id = _props$id === undefined ? 'Menu' : _props$id,
    _props$transparent = props.transparent,
    transparent = _props$transparent === undefined ? false : _props$transparent,
    menuHeader = props.menuHeader,
    rest = _objectWithoutProperties(props, _excluded);
  var theme = useTheme();
  var _useBoolean = useBoolean(),
    open = _useBoolean.value,
    toggleOpen = _useBoolean.toggle,
    setClosed = _useBoolean.setFalse;
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    menuOffset = _React$useState2[0],
    setMenuOffset = _React$useState2[1];
  var buttonRef = React.useRef(null);
  var handleClose = React.useCallback(function () {
    // istanbul ignore next
    if (buttonRef && buttonRef.current) {
      var btn = buttonRef.current;
      // istanbul ignore next
      setTimeout(function () {
        btn.focus();
      });
    }
    setClosed();
  }, [buttonRef, setClosed]);
  var _useOutsideClickClose = useOutsideClickClose(setClosed, !open),
    wrapperRef = _useOutsideClickClose.wrapperRef;
  var handleOnSelect = React.useCallback(function (value) {
    setClosed();
    onSelect(value);
  }, [setClosed, onSelect]);
  var expandedIcon = hideExpandIcon ? undefined : open ? /*#__PURE__*/React.createElement(IconChevronUp, null) : /*#__PURE__*/React.createElement(IconChevronDown, null);
  var leftIcon = iconPlacement === 'left' ? icon : expandedIcon;
  var rightIcon = iconPlacement === 'right' ? icon : expandedIcon;
  var variant = variantProp || theme.variant;
  var menuTheme = useComponentVariant(theme.menu.web, variant);
  var menuContainerRef = React.useRef(null);
  var color = colorProp || (transparent ? theme.tokens.button.desaturated : menuTheme.color);
  React.useEffect(function () {
    var menuContainer = menuContainerRef.current;
    var wrapper = wrapperRef.current;
    var update = function update() {
      if (open && menuContainer && wrapper) {
        var _menuContainer$getBou = menuContainer.getBoundingClientRect(),
          width = _menuContainer$getBou.width;
        var _wrapper$getBoundingC = wrapper.getBoundingClientRect(),
          x = _wrapper$getBoundingC.x;
        // istanbul ignore next
        if (width + x + 8 > window.innerWidth) {
          var offset = window.innerWidth - (width + x + 8);
          setMenuOffset(offset);
        } else {
          setMenuOffset(0);
        }
      }
    };
    window.addEventListener('resize', update);
    update();
    return function () {
      window.removeEventListener('resize', update);
    };
  }, [wrapperRef, menuContainerRef, open, setMenuOffset]);
  var listItemsContainerProps = dropdownDirection === 'down' ? {
    top: px2rem(menuTheme.height)
  } : {
    bottom: 0
  };
  return /*#__PURE__*/React.createElement(Box, _extends({
    boxRef: wrapperRef,
    display: "inline-flex",
    id: id,
    position: "relative"
  }, rest), showLabel ? /*#__PURE__*/React.createElement(Button, _extends({
    id: "elemental-Menu-trigger-".concat(id),
    status: "plain",
    "aria-label": ariaLabel || labelProp,
    "aria-live": "polite",
    color: color,
    ref: buttonRef,
    onClick: toggleOpen,
    leftIcon: leftIcon,
    rightIcon: rightIcon,
    variant: variant,
    transparent: transparent,
    "aria-haspopup": items.length > 0,
    "aria-expanded": open,
    "aria-controls": "elemental-Menu-options-".concat(id),
    paddingHorizontal: spacing.xxs
  }, buttonProps), labelProp) : icon && /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": ariaLabel || labelProp,
    status: "plain",
    color: color,
    onClick: toggleOpen,
    ref: buttonRef,
    size: menuTheme.height,
    icon: icon,
    transparent: transparent
  }), /*#__PURE__*/React.createElement(Box, {
    boxRef: menuContainerRef,
    position: "absolute",
    width: menuWidth,
    marginLeft: px2rem(menuOffset)
  }, /*#__PURE__*/React.createElement(ListItems, {
    id: "elemental-Menu-options-".concat(id),
    "aria-labelledby": "elemental-Menu-trigger-".concat(id),
    role: "menu",
    listItemRole: "menuitem",
    activateFirstItem: true,
    disableBorders: true,
    items: items,
    highlightSelected: true,
    opened: open,
    boxShadow: shadows.m,
    selected: selected,
    onClose: handleClose,
    onSelect: handleOnSelect,
    showItems: items.length,
    variant: variant,
    width: menuWidth,
    borderRadius: theme.borders.radius,
    containerProps: listItemsContainerProps,
    header: menuHeader
  })));
}

export { Menu };
